// 數學課題數據 - Mathematics Topics Data
export const mathTopics = [
  {
    id: 'M01',
    code: 'M01',
    title: '數系與近似值',
    titleEn: 'Number system & Approximation',
    description: '學習各種數系的性質和近似值的計算方法',
    color: 'bg-blue-500',
    icon: '🔢',
    modules: [
      {
        id: 'M01-1',
        title: '實數系統',
        description: '自然數、整數、有理數、無理數的概念',
        examples: [
          {
            id: 'M01-1-1',
            title: '自然數的性質',
            content: '自然數是最基本的數系，包含所有正整數。',
            formula: '\\mathbb{N} = \\{1, 2, 3, 4, 5, ...\\}',
            solution: '自然數用於計數，是數學的基礎。'
          },
          {
            id: 'M01-1-2',
            title: '整數的運算',
            content: '整數包括正整數、負整數和零。',
            formula: '\\mathbb{Z} = \\{..., -2, -1, 0, 1, 2, ...\\}',
            solution: '整數的加減乘除運算遵循特定規則。'
          },
          {
            id: 'M01-1-3',
            title: '有理數的表示',
            content: '有理數可以表示為兩個整數的比值。',
            formula: '\\mathbb{Q} = \\{\\frac{a}{b} : a, b \\in \\mathbb{Z}, b \\neq 0\\}',
            solution: '有理數包括所有可以表示為分數的數。'
          },
          {
            id: 'M01-1-4',
            title: '無理數的概念',
            content: '無理數不能表示為兩個整數的比值。',
            formula: '\\sqrt{2}, \\pi, e \\in \\mathbb{I}',
            solution: '無理數的小數表示是無限不循環小數。'
          },
          {
            id: 'M01-1-5',
            title: '實數的完備性',
            content: '實數系統包含所有有理數和無理數。',
            formula: '\\mathbb{R} = \\mathbb{Q} \\cup \\mathbb{I}',
            solution: '實數軸上的每一點都對應一個實數。'
          }
        ]
      },
      {
        id: 'M01-2',
        title: '近似值與誤差',
        description: '學習如何計算近似值和分析誤差',
        examples: [
          {
            id: 'M01-2-1',
            title: '四捨五入',
            content: '將數值四捨五入到指定位數。',
            formula: '3.14159 \\approx 3.14 \\text{（保留兩位小數）}',
            solution: '四捨五入是最常用的近似方法。'
          },
          {
            id: 'M01-2-2',
            title: '有效數字',
            content: '確定測量值的有效數字位數。',
            formula: '0.00123 \\text{有3個有效數字}',
            solution: '有效數字反映測量的精確度。'
          },
          {
            id: 'M01-2-3',
            title: '絕對誤差',
            content: '計算測量值與真實值的差。',
            formula: '|x - x_0| \\text{（絕對誤差）}',
            solution: '絕對誤差表示誤差的大小。'
          },
          {
            id: 'M01-2-4',
            title: '相對誤差',
            content: '計算誤差相對於真實值的比例。',
            formula: '\\frac{|x - x_0|}{|x_0|} \\times 100\\%',
            solution: '相對誤差表示誤差的相對大小。'
          },
          {
            id: 'M01-2-5',
            title: '科學記數法',
            content: '用科學記數法表示很大或很小的數。',
            formula: '123000 = 1.23 \\times 10^5',
            solution: '科學記數法便於表示極值。'
          }
        ]
      }
    ]
  },
  {
    id: 'M02',
    code: 'M02',
    title: '恆等式',
    titleEn: 'Identities',
    description: '學習代數恆等式的性質和應用',
    color: 'bg-green-500',
    icon: '🔀',
    modules: [
      {
        id: 'M02-1',
        title: '基本恆等式',
        description: '掌握基本的代數恆等式',
        examples: [
          {
            id: 'M02-1-1',
            title: '平方差公式',
            content: '兩數平方的差等於兩數和與差的積。',
            formula: 'a^2 - b^2 = (a+b)(a-b)',
            solution: '這是最基本的因式分解公式。'
          },
          {
            id: 'M02-1-2',
            title: '完全平方公式',
            content: '兩數和的平方展開。',
            formula: '(a+b)^2 = a^2 + 2ab + b^2',
            solution: '完全平方式的標準形式。'
          },
          {
            id: 'M02-1-3',
            title: '完全平方差公式',
            content: '兩數差的平方展開。',
            formula: '(a-b)^2 = a^2 - 2ab + b^2',
            solution: '注意符號的變化。'
          },
          {
            id: 'M02-1-4',
            title: '立方和公式',
            content: '兩數立方的和的因式分解。',
            formula: 'a^3 + b^3 = (a+b)(a^2-ab+b^2)',
            solution: '立方和的標準分解形式。'
          },
          {
            id: 'M02-1-5',
            title: '立方差公式',
            content: '兩數立方的差的因式分解。',
            formula: 'a^3 - b^3 = (a-b)(a^2+ab+b^2)',
            solution: '立方差的標準分解形式。'
          }
        ]
      },
      {
        id: 'M02-2',
        title: '三角恆等式',
        description: '學習三角函數的恆等式',
        examples: [
          {
            id: 'M02-2-1',
            title: '畢氏恆等式',
            content: '正弦和餘弦的平方和等於1。',
            formula: '\\sin^2\\theta + \\cos^2\\theta = 1',
            solution: '這是最基本的三角恆等式。'
          },
          {
            id: 'M02-2-2',
            title: '和角公式（正弦）',
            content: '兩角和的正弦公式。',
            formula: '\\sin(A+B) = \\sin A \\cos B + \\cos A \\sin B',
            solution: '用於計算複合角的三角函數值。'
          },
          {
            id: 'M02-2-3',
            title: '和角公式（餘弦）',
            content: '兩角和的餘弦公式。',
            formula: '\\cos(A+B) = \\cos A \\cos B - \\sin A \\sin B',
            solution: '注意符號與正弦公式的差異。'
          },
          {
            id: 'M02-2-4',
            title: '二倍角公式',
            content: '角度加倍的三角函數公式。',
            formula: '\\sin 2\\theta = 2\\sin\\theta \\cos\\theta',
            solution: '二倍角公式是和角公式的特殊情況。'
          },
          {
            id: 'M02-2-5',
            title: '半角公式',
            content: '角度減半的三角函數公式。',
            formula: '\\sin^2\\frac{\\theta}{2} = \\frac{1-\\cos\\theta}{2}',
            solution: '半角公式用於計算特殊角的值。'
          }
        ]
      }
    ]
  },
  {
    id: 'M03',
    code: 'M03',
    title: '聯立二元一次方程',
    titleEn: 'Simultaneous linear equations',
    description: '學習解聯立二元一次方程的各種方法',
    color: 'bg-purple-500',
    icon: '⚖️',
    modules: [
      {
        id: 'M03-1',
        title: '代入法',
        description: '使用代入法解聯立方程',
        examples: [
          {
            id: 'M03-1-1',
            title: '基本代入法',
            content: '從一個方程式中解出一個未知數，代入另一個方程式。',
            formula: '\\begin{cases} x + y = 5 \\\\ 2x - y = 1 \\end{cases}',
            solution: '由第一式得 y = 5 - x，代入第二式得 x = 2, y = 3'
          },
          {
            id: 'M03-1-2',
            title: '複雜代入法',
            content: '處理係數較複雜的聯立方程。',
            formula: '\\begin{cases} 3x + 2y = 12 \\\\ x - 4y = -2 \\end{cases}',
            solution: '由第二式得 x = 4y - 2，代入第一式求解。'
          },
          {
            id: 'M03-1-3',
            title: '分數係數',
            content: '處理含有分數係數的聯立方程。',
            formula: '\\begin{cases} \\frac{x}{2} + \\frac{y}{3} = 1 \\\\ \\frac{x}{4} - \\frac{y}{6} = 0 \\end{cases}',
            solution: '先通分化簡，再使用代入法。'
          },
          {
            id: 'M03-1-4',
            title: '無解情況',
            content: '識別無解的聯立方程組。',
            formula: '\\begin{cases} 2x + 3y = 6 \\\\ 4x + 6y = 10 \\end{cases}',
            solution: '兩方程式矛盾，無解。'
          },
          {
            id: 'M03-1-5',
            title: '無限解情況',
            content: '識別有無限解的聯立方程組。',
            formula: '\\begin{cases} x + 2y = 4 \\\\ 2x + 4y = 8 \\end{cases}',
            solution: '兩方程式實質相同，有無限解。'
          }
        ]
      },
      {
        id: 'M03-2',
        title: '消元法',
        description: '使用消元法解聯立方程',
        examples: [
          {
            id: 'M03-2-1',
            title: '加減消元法',
            content: '通過加減消除一個未知數。',
            formula: '\\begin{cases} 3x + 2y = 7 \\\\ 2x - 2y = 3 \\end{cases}',
            solution: '兩式相加消除 y，得 5x = 10，所以 x = 2'
          },
          {
            id: 'M03-2-2',
            title: '乘法消元法',
            content: '先將係數調整為相等，再消元。',
            formula: '\\begin{cases} 2x + 3y = 8 \\\\ 3x - 2y = 1 \\end{cases}',
            solution: '第一式乘2，第二式乘3，然後相加消除 y。'
          },
          {
            id: 'M03-2-3',
            title: '係數互質消元',
            content: '處理係數互質的情況。',
            formula: '\\begin{cases} 5x + 7y = 23 \\\\ 3x - 4y = 1 \\end{cases}',
            solution: '找到適當的倍數使係數相等。'
          },
          {
            id: 'M03-2-4',
            title: '負係數處理',
            content: '處理含有負係數的方程組。',
            formula: '\\begin{cases} -2x + 5y = 3 \\\\ 4x - 3y = 7 \\end{cases}',
            solution: '注意符號的處理，避免計算錯誤。'
          },
          {
            id: 'M03-2-5',
            title: '三元一次方程',
            content: '擴展到三元一次方程組。',
            formula: '\\begin{cases} x + y + z = 6 \\\\ 2x - y + z = 1 \\\\ x + 2y - z = 5 \\end{cases}',
            solution: '逐步消元，先消除一個變數。'
          }
        ]
      }
    ]
  },
  {
    id: 'M04',
    code: 'M04',
    title: '角、平行線、三角形與四邊形',
    titleEn: 'Angles, parallel lines, triangles and quadrilaterals',
    description: '學習幾何圖形的基本性質和定理',
    color: 'bg-red-500',
    icon: '📐',
    modules: [
      {
        id: 'M04-1',
        title: '角的性質',
        description: '學習各種角的性質和關係',
        examples: [
          {
            id: 'M04-1-1',
            title: '對頂角',
            content: '兩條直線相交形成的對頂角相等。',
            formula: '\\angle AOB = \\angle COD',
            solution: '對頂角是幾何的基本性質。'
          },
          {
            id: 'M04-1-2',
            title: '鄰角',
            content: '相鄰的兩個角之和等於平角。',
            formula: '\\angle AOB + \\angle BOC = 180°',
            solution: '鄰角的和等於180度。'
          },
          {
            id: 'M04-1-3',
            title: '餘角',
            content: '兩角之和等於90度。',
            formula: '\\angle A + \\angle B = 90°',
            solution: '餘角的概念在直角三角形中很重要。'
          },
          {
            id: 'M04-1-4',
            title: '補角',
            content: '兩角之和等於180度。',
            formula: '\\angle A + \\angle B = 180°',
            solution: '補角的概念在平行線中很重要。'
          },
          {
            id: 'M04-1-5',
            title: '周角',
            content: '一個完整的圓周角等於360度。',
            formula: '\\text{周角} = 360°',
            solution: '周角是角度測量的基準。'
          }
        ]
      },
      {
        id: 'M04-2',
        title: '三角形性質',
        description: '學習三角形的基本性質和定理',
        examples: [
          {
            id: 'M04-2-1',
            title: '內角和定理',
            content: '三角形的內角和等於180度。',
            formula: '\\angle A + \\angle B + \\angle C = 180°',
            solution: '這是三角形最基本的性質。'
          },
          {
            id: 'M04-2-2',
            title: '外角定理',
            content: '三角形的外角等於不相鄰的兩個內角之和。',
            formula: '\\angle ACD = \\angle A + \\angle B',
            solution: '外角定理是內角和定理的推論。'
          },
          {
            id: 'M04-2-3',
            title: '等腰三角形',
            content: '等腰三角形的兩個底角相等。',
            formula: '\\text{若 } AB = AC\\text{，則 }\\angle B = \\angle C',
            solution: '等腰三角形的對稱性質。'
          },
          {
            id: 'M04-2-4',
            title: '等邊三角形',
            content: '等邊三角形的三個角都等於60度。',
            formula: '\\angle A = \\angle B = \\angle C = 60°',
            solution: '等邊三角形是特殊的等腰三角形。'
          },
          {
            id: 'M04-2-5',
            title: '直角三角形',
            content: '直角三角形有一個90度的角。',
            formula: '\\angle C = 90°\\text{，}\\angle A + \\angle B = 90°',
            solution: '直角三角形的兩個銳角互為餘角。'
          }
        ]
      }
    ]
  },
  {
    id: 'M05',
    code: 'M05',
    title: '百分法',
    titleEn: 'Percentages',
    description: '學習百分比的計算和應用',
    color: 'bg-yellow-500',
    icon: '💰',
    modules: [
      {
        id: 'M05-1',
        title: '百分比基礎',
        description: '學習百分比的基本概念和計算',
        examples: [
          {
            id: 'M05-1-1',
            title: '百分比的定義',
            content: '百分比表示一個數是另一個數的百分之幾。',
            formula: '\\text{百分比} = \\frac{\\text{部分}}{\\text{整體}} \\times 100\\%',
            solution: '百分比是比例的另一種表示方法。'
          },
          {
            id: 'M05-1-2',
            title: '分數轉百分比',
            content: '將分數轉換為百分比。',
            formula: '\\frac{3}{4} = \\frac{3}{4} \\times 100\\% = 75\\%',
            solution: '分數乘以100%即可轉換。'
          },
          {
            id: 'M05-1-3',
            title: '小數轉百分比',
            content: '將小數轉換為百分比。',
            formula: '0.25 = 0.25 \\times 100\\% = 25\\%',
            solution: '小數乘以100%即可轉換。'
          },
          {
            id: 'M05-1-4',
            title: '百分比轉分數',
            content: '將百分比轉換為最簡分數。',
            formula: '60\\% = \\frac{60}{100} = \\frac{3}{5}',
            solution: '百分比除以100%，然後約分。'
          },
          {
            id: 'M05-1-5',
            title: '百分比轉小數',
            content: '將百分比轉換為小數。',
            formula: '35\\% = \\frac{35}{100} = 0.35',
            solution: '百分比除以100%即可轉換。'
          }
        ]
      },
      {
        id: 'M05-2',
        title: '百分比應用',
        description: '學習百分比在實際問題中的應用',
        examples: [
          {
            id: 'M05-2-1',
            title: '折扣計算',
            content: '計算商品打折後的價格。',
            formula: '\\text{售價} = \\text{原價} \\times (1 - \\text{折扣率})',
            solution: '8折即20%的折扣。'
          },
          {
            id: 'M05-2-2',
            title: '增長率計算',
            content: '計算數量的增長百分比。',
            formula: '\\text{增長率} = \\frac{\\text{新值} - \\text{舊值}}{\\text{舊值}} \\times 100\\%',
            solution: '增長率可以是正數或負數。'
          },
          {
            id: 'M05-2-3',
            title: '稅率計算',
            content: '計算含稅價格或稅額。',
            formula: '\\text{含稅價} = \\text{原價} \\times (1 + \\text{稅率})',
            solution: '稅率通常以百分比表示。'
          },
          {
            id: 'M05-2-4',
            title: '利息計算',
            content: '計算簡單利息。',
            formula: '\\text{利息} = \\text{本金} \\times \\text{利率} \\times \\text{時間}',
            solution: '利率通常以年利率表示。'
          },
          {
            id: 'M05-2-5',
            title: '複利計算',
            content: '計算複利的本利和。',
            formula: 'A = P(1 + r)^n',
            solution: 'A是本利和，P是本金，r是利率，n是期數。'
          }
        ]
      }
    ]
  }
];

// 數學分類
export const mathCategories = [
  { id: 'all', name: '全部課題', color: 'bg-gray-500' },
  { id: 'algebra', name: '代數', color: 'bg-blue-500' },
  { id: 'geometry', name: '幾何', color: 'bg-green-500' },
  { id: 'statistics', name: '統計', color: 'bg-purple-500' },
  { id: 'trigonometry', name: '三角學', color: 'bg-red-500' },
  { id: 'calculus', name: '微積分', color: 'bg-yellow-500' }
];

// 數學表達式庫
export const mathExpressions = {
  basic: [
    { latex: 'x^2 + y^2 = r^2', description: '圓的方程式' },
    { latex: 'a^2 + b^2 = c^2', description: '畢氏定理' },
    { latex: '\\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}', description: '二次方程公式' },
    { latex: '\\sin^2\\theta + \\cos^2\\theta = 1', description: '三角恆等式' },
    { latex: '\\lim_{x \\to 0} \\frac{\\sin x}{x} = 1', description: '極限' }
  ],
  advanced: [
    { latex: '\\int_a^b f(x)dx = F(b) - F(a)', description: '定積分基本定理' },
    { latex: '\\sum_{n=1}^{\\infty} \\frac{1}{n^2} = \\frac{\\pi^2}{6}', description: '巴塞爾問題' },
    { latex: 'e^{i\\pi} + 1 = 0', description: '歐拉公式' },
    { latex: '\\nabla \\times \\mathbf{F} = \\left(\\frac{\\partial F_z}{\\partial y} - \\frac{\\partial F_y}{\\partial z}\\right)\\mathbf{i}', description: '旋度' }
  ]
};

export default mathTopics; 